import axios from 'axios'
import env from '../../constants/env'
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '../../state/authToken'

export const API_TYPE = {
  JSON: 'JSON',
  BLOB: 'BLOB',
}

let isRefreshing = false
let refreshSubscribers = []

const subscribeRefresh = (callback) => {
  refreshSubscribers.push(callback)
}

const notifyRefreshSubscribers = (token) => {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

const AivatarClient = ({type = API_TYPE.JSON} = {}) => {
  const axiosInstance = axios.create({
    baseURL: `${env.API_HOST}/api/v1`,
    responseType: type === API_TYPE.JSON ? 'json' : 'blob',
    withCredentials: true,
  })

  axiosInstance.interceptors.request.use((config) => {
    const token = getAccessToken()
    if (!token) return config

    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    }
  })

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const {response, config} = error

      if (!response || response.status !== 401 || config?._retry) {
        return Promise.reject(error)
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeRefresh((token) => {
            if (!token) {
              reject(error)
              return
            }

            const retryConfig = {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
              },
              _retry: true,
            }

            resolve(axiosInstance(retryConfig))
          })
        })
      }

      isRefreshing = true

      try {
        const refreshResponse = await axios.post(
          `${env.API_HOST}/api/v1/auth/refresh`,
          {},
          {withCredentials: true},
        )

        const newAccessToken = refreshResponse.data.accessToken
        setAccessToken(newAccessToken)
        notifyRefreshSubscribers(newAccessToken)

        const retryConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
          _retry: true,
        }

        return await axiosInstance(retryConfig)
      } catch (refreshError) {
        clearAccessToken()
        notifyRefreshSubscribers(null)
        return await Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    },
  )

  return axiosInstance
}

export default AivatarClient
